import { 
  Vector3, 
  Color, 
  BufferGeometry,
  BufferAttribute,
  Points,
  ShaderMaterial,
  AdditiveBlending
} from 'three';

import {
  RADIUS_GALAXY,
  STARS_MAX_SPEED,
  STARS_MIN_SPEED,
  INCLIN_GALAXY,
  STARS_MAX_OFFSET,
  STARS_MIN_OFFSET,
  WAVE_SPEED
} from '../configEntities/galaxyConfig.js';
import { loading } from '../loading/loading.js';
import { pushShift } from '../groupsEntities/entitiesGroups.js';
import { attrStars } from '../configEntities/groupsAttr.js';
import maskGalaxyImg from '../../../assets/textures/galaxy/mask_milk.png';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer'
import vertexStars from '../../../shaders/stars/vertex.glsl';
import fragmentStars from '../../../shaders/stars/fragment.glsl';

import { showInfoPanel } from '../ui.js';

import {sceneManager } from '../script.js';
import { animatePartsGalaxy } from '../utils/many-bodies.js';

export class Galaxy {
  constructor(centerElement) {
    this.name = 'Milky Way';
    this.numPoints = 10000;
    this.blending = AdditiveBlending;
    this.centerElement = centerElement
    this.speeds = [];
    this.inclSpeed = 2e-10
    this.warpPhase = 0
    this.maskTexture = loading.textureLoader.load(
      maskGalaxyImg,
      () => this._initGeometry()
    );
  }
  _initGeometry() {
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = this.maskTexture.image.width;
    maskCanvas.height = this.maskTexture.image.height;
    const maskCtx = maskCanvas.getContext('2d');
    maskCtx.translate(maskCanvas.width / 2, maskCanvas.height / 2);
    maskCtx.rotate(Math.PI);
    maskCtx.drawImage(this.maskTexture.image, -maskCanvas.width / 2, -maskCanvas.height / 2);
    const maskData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height).data;
    const canvasWidth = maskCanvas.width;
    const canvasHeight = maskCanvas.height;
    const positions = [];
    const sizes = [];
    const shift = []
    const colors = [];

    const u_r = RADIUS_GALAXY;

    const getMaskBrightness = (xLocal, zLocal) => {
      const uvx = (xLocal / (u_r * 2)) + 0.5;
      const uvz = (zLocal / (u_r * 2)) + 0.5;
      const ix = Math.min(Math.max(Math.floor(uvx * canvasWidth), 0), canvasWidth - 1);
      const iy = Math.min(Math.max(Math.floor(uvz * canvasHeight), 0), canvasHeight - 1);
      const idx = (iy * canvasWidth + ix) * 4;
      const r = maskData[idx], g = maskData[idx + 1], b = maskData[idx + 2];
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    };

    const smoothstep = (edge0, edge1, x, exp = 1.0) => {
      let t = (x - edge0) / (edge1 - edge0);
      t = Math.max(0, Math.min(1, t));
      t = Math.pow(t, exp);
      return t * t * (3 - 2 * t);
    };

    for (let i = 0; i < this.numPoints; i++) {
      const r = Math.pow(Math.random(), 0.8) * RADIUS_GALAXY;
      const theta = Math.random() * Math.PI * 2;
      const xLocal = r * Math.cos(theta);
      const zLocal = r * Math.sin(theta);

      const dUV = r / RADIUS_GALAXY;
      const radial1 = 1 - smoothstep(0, 0.25, dUV, 1.5);
      const radial2 = 1 - smoothstep(0, 1, dUV, 5);
      const yBase = STARS_MAX_OFFSET * radial1 + STARS_MIN_OFFSET * radial2;
      const yLocal = (Math.random() * 2 - 1) * yBase;

      const brightness = getMaskBrightness(xLocal, zLocal);
      const probability = 0.08 + 0.92 * brightness;

      if (Math.random() <= probability) {
        positions.push(xLocal, yLocal, zLocal);

        const { color, size } = colorSizeAleat(attrStars);
        sizes.push(size*2000000000);
        colors.push(color.r, color.g, color.b);

        this.speeds.push(Math.random() * (STARS_MAX_SPEED - STARS_MIN_SPEED) + STARS_MIN_SPEED);
        pushShift(shift, RADIUS_GALAXY / 50);
      }
    }
    this.geometry = new BufferGeometry();
    this.geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
    this.geometry.setAttribute('sizes', new BufferAttribute(new Float32Array(sizes), 1));
    this.geometry.setAttribute('color', new BufferAttribute(new Float32Array(colors), 3));

    this.geometry.setAttribute('shift', new BufferAttribute(new Float32Array(shift), 4))

    const material = new ShaderMaterial({
      uniforms: {
        opacity: { value: 1.0 },
        time: { value: 0 },
        glowIntensity: { value: 0.8 },  
        coreSharpness: { value: 5.0 } 
      },
      vertexShader: vertexStars,
      fragmentShader: fragmentStars,
      blending: this.blending,
      transparent: false,
      depthWrite: false,
      depthTest: false
    });

  
    this.points = new Points(this.geometry, material);
    this.points.renderOrder = 0
    this.points.rotation.x = INCLIN_GALAXY;

    this.posLabel = this.labelPosition()

    this.div = document.createElement('div');
    this.div.className = 'label';
    this.div.textContent = this.name;
    this.div.style.backgroundColor = 'transparent';
    this.div.style.cursor = 'pointer';
    this.infoIcon = document.createElement('i');
    this.infoIcon.className = 'fas fa-info-circle info-icon';
    this.infoIcon.style.pointerEvents = 'auto';
    this.infoIcon.title = 'More Information'
    this.div.appendChild(this.infoIcon);

    this.infoIcon.addEventListener('click', (evt) => {
    evt.stopPropagation();
    showInfoPanel(this.name);
    });

    const canvas = sceneManager.renderer.domElement;
    this.div.addEventListener('wheel', e => {
      e.preventDefault();        
      const evt = new WheelEvent('wheel', e);
      canvas.dispatchEvent(evt);    
    });

    this.label = new CSS2DObject(this.div);
    this.label.center.set(0, 1)
    sceneManager.scene.add(this.label);
    this.label.position.copy(this.posLabel)
    this.points.add(this.label)
    this.label.layers.set(0)

    this.originalPositions = this.geometry.attributes.position.array.slice();

    sceneManager.scene.add(this.points);
  }
  labelPosition() {
    return new Vector3(
      this.centerElement.mesh.position.x/3,
      this.centerElement.mesh.position.y-RADIUS_GALAXY/3,
      this.centerElement.mesh.position.z+RADIUS_GALAXY/3
    );
  }
    centerOrbit() {
      this.points.position.copy(this.centerElement.mesh.position)
    }

  animate(speed) {
    if (!this.points) return;

    this.warpPhase = (this.warpPhase + speed * WAVE_SPEED) % (2 * Math.PI);

    this.centerOrbit()
    animatePartsGalaxy(
      this.points,
      speed,
      this.warpPhase,
      this.speeds,
      this.originalPositions
    );

    const camPos = sceneManager.camera.position;
    const distance = camPos.length()

    this.label.visible = sceneManager.commands.controls.labels && (distance > 1e7);
    this.points.material.uniforms.time.value += speed*this.inclSpeed
  }
}
export function colorSizeAleat(attr) {
  const total = attr.percentage.reduce((a, b) => a + b, 0);
  const rnd = Math.random() * total;
  let sum = 0,
    idx = 0;
  for (let i = 0; i < attr.percentage.length; i++) {
    sum += attr.percentage[i];
    if (rnd <= sum) {
      idx = i;
      break;
    }
  }
  const col = attr.color[idx];
  const r = (col >> 16) & 255,
    g = (col >> 8) & 255,
    b = col & 255;
  return { color: new Color(r / 255, g / 255, b / 255), size: attr.size[idx] };
}
